import styled from '@emotion/styled';
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

type ButtonColor = 'primary' | 'secondary' | 'error' | 'success' | 'info';

export interface ButtonProps extends Omit<MuiButtonProps, 'color'> {
    color?: ButtonColor;
    isLoading?: boolean;
    loadingText?: string;
}

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StyledCircularProgress = styled(CircularProgress, { shouldForwardProp: propName => propName !== 'dark' })<{ dark: boolean }>`
    color: ${({ dark }) => (dark ? '#000' : '#fff')};
`;

const StyledButton = styled(MuiButton)`
    text-transform: none;
    border-radius: 8px;
    display: flex;
    align-items: center;
    height: 4rem;

    &.MuiButton-contained.MuiButton-containedInfo {
        color: black !important;
        background-color: #fff !important;
    }

    &.MuiButton-contained:hover {
        background: ${({ theme, color }) => theme.colors[(color as ButtonColor) || 'primary']['hover']} !important;
    }
    &.MuiButton-outlined:disabled {
        border-color: ${({ theme, color }) => theme.colors[(color as ButtonColor) || 'primary']['main']} !important;
    }
    &.MuiButton-text:disabled,
    &.MuiButton-outlined:disabled {
        opacity: 0.5;
        color: ${({ theme, color }) => theme.colors[(color as ButtonColor) || 'primary']['main']} !important;
    }
    &.MuiButton-contained:disabled {
        opacity: 0.5;
        background: ${({ theme, color }) => theme.colors[(color as ButtonColor) || 'primary']['main']} !important;
        color: ${({ color, theme }) => (color === 'primary' ? theme.colors.black.main : '#fff')};
    }

    .MuiButton-contained {
        color: ${({ color, theme }) => (color === 'primary' ? theme.colors.black.main : '#fff')} !important;
    }
`;

function Button({
    children,
    isLoading,
    loadingText,
    color = 'primary',
    disabled,
    variant = 'contained',
    startIcon,
    endIcon,
    ...buttonProps
}: ButtonProps) {
    const textDark = color === 'primary' || variant !== 'contained';

    function renderContent() {
        if (!isLoading) {
            if (typeof children === 'string') {
                return <div>{children}</div>;
            }

            return children;
        }

        return (
            <LoaderWrapper>
                <StyledCircularProgress color="inherit" size={16} dark={textDark} />
                {loadingText}
            </LoaderWrapper>
        );
    }

    return (
        <StyledButton
            {...buttonProps}
            color={color}
            variant={variant}
            disabled={disabled || isLoading}
            disableElevation
            startIcon={!isLoading && startIcon}
            endIcon={!isLoading && endIcon}>
            {renderContent()}
        </StyledButton>
    );
}

export default Button;
